/**
 * Application shell — sidebar and topbar, matching docs/prototype.html.
 *
 * The sidebar is deliberately "locked" in the prototype's language: dark blue,
 * the Front Porch wordmark, a red active bar. It matches the Receipts app so
 * the two feel like one suite rather than two products. Everything else in
 * the app renders inside `.content`.
 *
 * Nav visibility follows role. An FPS Super Admin gets the platform level
 * (Clients, Users) above the client workspace; a client admin never sees it,
 * because those surfaces answer 404 for them server-side anyway and offering
 * a link that 404s is worse than offering none.
 */

import { T } from '../lib/copyEdit.tsx';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { authClient, useSession } from '../lib/auth.ts';
import Wordmark from './Wordmark.tsx';
import LegalFooter from './LegalFooter.tsx';
import { CopyEditProvider } from '../lib/copyEdit.tsx';

type NavKey = 'clients' | 'dashboard' | 'events' | 'library' | 'settings' | 'users';

/** What the rail needs to draw one client. A subset of the clients endpoint. */
export interface RailClient {
  id: string;
  name: string;
  primaryColor: string;
  appIconData?: string | null;
  counts?: { pendingReview?: number } | null;
}

interface Props {
  active: NavKey;
  /** Describes where you are, at the left of the topbar. */
  context?: ReactNode;
  /**
   * The client switcher, at the RIGHT of the topbar.
   *
   * Its own slot rather than more `context`, because the two halves of this bar
   * do different jobs: the left says where you are and the right is the one
   * control that changes it. The switcher used to sit on the left, where it
   * read as a label rather than something you could press.
   */
  switcher?: ReactNode;
  children: ReactNode;
  /** Present when inside a client workspace; absent at the platform level. */
  clientId?: string;
  /**
   * The clients for the rail.
   *
   * Passed in by Workspace, which already loads them to validate the id in the
   * URL, and fetched here otherwise — Settings, Users and the Clients list have
   * no reason to load them for their own sake, and the whole point of the rail
   * is that it is on those screens too.
   */
  clients?: RailClient[];
}

function initials(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const ROLE_LABEL: Record<string, string> = {
  fps_super_admin: 'FPS Admin',
  client_admin: 'Client Admin',
};

export default function AppShell({
  active,
  context,
  switcher,
  children,
  clientId,
  clients: given,
}: Props) {
  const { data: session } = useSession();
  const user = session?.user as { name?: string; email?: string; role?: string } | undefined;
  const isPlatformAdmin = user?.role === 'fps_super_admin';

  const [fetched, setFetched] = useState<RailClient[] | null>(null);

  useEffect(() => {
    /* Only when nobody handed them over, so a workspace screen does not load
       the same list twice. */
    if (given) return;
    let live = true;
    void fetch('/api/clients')
      .then((r) => (r.ok ? r.json() : { clients: [] }))
      .then((b: { clients: RailClient[] }) => live && setFetched(b.clients ?? []))
      .catch(() => live && setFetched([]));
    return () => {
      live = false;
    };
  }, [given]);

  const clients = given ?? fetched ?? [];
  /*
   * ONLY WHERE THERE IS A CHOICE TO MAKE.
   *
   * A client admin with one organization gets no rail: a list of one is not
   * navigation, it is decoration, and it would take height from the nav that
   * actually moves them around. They keep the static name in the topbar.
   */
  const showRail = clients.length > 1;

  return (
    <CopyEditProvider enabled={isPlatformAdmin}>
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <Wordmark />
        </div>
        <div className="sidebar-app-name"><T k="common.appName" d="Sponsor Logos" /></div>

        {/* THE CLIENTS, in the rail rather than behind a dropdown.
            They used to live only in the topbar switcher, which Workspace
            renders — so on Clients, Settings and Users the top right was empty
            and there was no way to jump into a client at all. Those are the
            screens you land on, which is why picking a card felt like the only
            door. */}
        {showRail && (
          <div className="crail">
            <p className="crail-label">
              <T k="shell.rail.label" d="Clients" />
            </p>
            {clients.map((c) => (
              <ClientRow key={c.id} client={c} current={c.id === clientId} />
            ))}
            {isPlatformAdmin && (
              <Link
                className={`crail-all${active === 'clients' ? ' on' : ''}`}
                to="/"
              >
                <span className="sw sw--all">⋯</span>
                <T k="shell.rail.all" d="All clients" />
              </Link>
            )}
          </div>
        )}

        {showRail && <div className="nav-sep" />}

        <nav className="snav">
          {isPlatformAdmin && !showRail && (
            <>
              <NavItem icon="grid" label="Clients" to="/" active={active === 'clients'} />
              <div className="nav-sep" />
            </>
          )}
          <NavItem
            icon="dashboard"
            label="Dashboard"
            active={active === 'dashboard'}
            to={clientId ? `/c/${clientId}` : undefined}
          />
          <NavItem
            icon="calendar"
            label="Events"
            active={active === 'events'}
            to={clientId ? `/c/${clientId}/events` : undefined}
          />
          <NavItem
            icon="image"
            label="Logo Library"
            active={active === 'library'}
            to={clientId ? `/c/${clientId}/library` : undefined}
          />
        </nav>

        <div className="sidebar-foot">
          {isPlatformAdmin && (
            <NavItem icon="cog" label="Settings" to="/settings" active={active === 'settings'} />
          )}
          {isPlatformAdmin && (
            <NavItem icon="users" label="Users" to="/users" active={active === 'users'} />
          )}
          <div className="suser">
            <span className="uav">{initials(user?.name ?? user?.email ?? '?')}</span>
            <div>
              <div className="n">{user?.name ?? user?.email}</div>
              <div className="r">{ROLE_LABEL[user?.role ?? ''] ?? 'Member'}</div>
            </div>
            <button
              type="button"
              className="logout"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => {
                void authClient.signOut().then(() => window.location.assign('/login'));
              }}
            >
              <Icon name="signout" />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <div className="tb-left">
            {context && <span className="tb-context">{context}</span>}
          </div>
          {switcher && <div className="tb-right">{switcher}</div>}
        </div>
        <div className="content">{children}</div>
        <LegalFooter variant="app" />
      </div>
    </div>
    </CopyEditProvider>
  );
}

/**
 * One client in the rail.
 *
 * CARRIES ITS REVIEW COUNT, which is the reason this is worth more than a
 * tidier switcher. A list of names is navigation; a list of names with "7"
 * beside one of them answers "where does my attention need to go" before
 * anybody has opened anything. The number is already on the clients endpoint,
 * so it costs nothing to show.
 *
 * The mark is the FETCHED ICON rather than the uploaded logo, for the same
 * reason ClientSwitcher uses it: the uploaded logo is a wide lockup built for
 * an email header and would shrink to a smear at 18px.
 */
function ClientRow({ client, current }: { client: RailClient; current: boolean }) {
  const waiting = client.counts?.pendingReview ?? 0;

  return (
    <Link
      className={`crow${current ? ' on' : ''}`}
      to={`/c/${client.id}`}
      aria-current={current ? 'true' : undefined}
      title={waiting > 0 ? `${client.name} · ${waiting} awaiting a decision` : client.name}
    >
      {client.appIconData ? (
        <span className="sw sw--icon">
          <img src={client.appIconData} alt="" />
        </span>
      ) : (
        <span className="sw" style={{ background: client.primaryColor }}>
          {initials(client.name)}
        </span>
      )}
      <span className="crow-name">{client.name}</span>
      {waiting > 0 && <span className="cnt">{waiting}</span>}
    </Link>
  );
}

function NavItem({
  icon,
  label,
  active,
  to,
  disabled,
}: {
  icon: IconName;
  label: string;
  active: boolean;
  to?: string;
  disabled?: boolean;
}) {
  const className = `nav-item${active ? ' active' : ''}${disabled || !to ? ' nav-item--soon' : ''}`;

  // Not yet built. Rendered rather than hidden so the shape of the app stays
  // legible, but inert so nothing leads to a dead screen.
  if (!to || disabled) {
    return (
      <span className={className} aria-disabled title="Coming next">
        <Icon name={icon} />
        {label}
      </span>
    );
  }

  return (
    <Link className={className} to={to}>
      <Icon name={icon} />
      {label}
    </Link>
  );
}

type IconName = 'grid' | 'dashboard' | 'calendar' | 'image' | 'cog' | 'users' | 'signout';

/** Lucide paths, matching the prototype. No emoji anywhere in FPS UI. */
function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    grid: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 3v18M3 9h6" />
      </>
    ),
    dashboard: (
      <>
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </>
    ),
    image: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.6-3.6a2 2 0 0 0-2.8 0L6 20" />
      </>
    ),
    cog: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </>
    ),
    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
    signout: (
      <>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5M21 12H9" />
      </>
    ),
  };

  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}
